/* Opt-in enhancement: original Shopify cards and mix/cart remain authoritative. */
(() => {
  'use strict';
  class OriginalsRecordBin extends HTMLElement {
    async connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.abort = new AbortController();
      this.hiddenElements = [];
      const timeout = setTimeout(() => this.abort.abort(), 20000);
      try {
        const base = new URL(this.dataset.collectionUrl, location.origin);
        if (base.origin !== location.origin) throw Error('Invalid collection origin');
        const sort = new URL(location.href).searchParams.get('sort_by');
        if (sort) base.searchParams.set('sort_by', sort);
        const products = new Map(), cards = new Map(), visited = new Set();
        const initialScope = this.closest('[data-mix-scope]');
        const initialData = initialScope.querySelector('[data-record-bin-catalog]');
        const initialCatalog = initialScope.querySelector('[data-surprise-catalog]');
        // The unfiltered initial response already contains the complete catalog.
        let initialPage = initialData && initialCatalog && JSON.parse(initialData.textContent).length === Number(this.dataset.count) ? initialScope : null;
        let next = base.href;
        while (next) {
          const url = new URL(next, location.origin);
          if (url.origin !== base.origin || url.pathname !== base.pathname || visited.has(url.href) || visited.size >= 250) throw Error('Incomplete collection');
          visited.add(url.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          let page = initialPage;
          initialPage = null;
          if (!page) {
            const response = await fetch(url, { signal: this.abort.signal, credentials: 'same-origin' });
            if (!response.ok) throw Error('Collection unavailable');
            page = new DOMParser().parseFromString(await response.text(), 'text/html');
          }
          const data = page.querySelector('[data-record-bin-catalog]');
          const catalog = page.querySelector('[data-surprise-catalog]');
          if (!data || !catalog) throw Error('Catalog metadata unavailable');
          for (const product of JSON.parse(data.textContent)) {
            product.id = String(product.id); product.variantId = String(product.variantId);
            if (!/^\d+$/.test(product.id) || !/^\d+$/.test(product.variantId) || products.has(product.id)) throw Error('Invalid or duplicate product');
            const button = catalog.querySelector(`[data-original-mix-add][data-design-id="${product.variantId}"]`);
            const card = button?.closest('article')?.cloneNode(true);
            const face = card?.querySelector('.bs-cascade-face');
            if (!card || !face || !product.image?.url) throw Error('Original product card missing');
            product.image.url = new URL(product.image.url, location.origin).href;
            product.image.crop = Object.fromEntries(['w','h','x','y'].map(key => [key, face.style.getPropertyValue(`--image-${key}`).trim()]));
            // Preserve exact crop values produced by the current theme.
            product.crop = 'full';
            products.set(product.id, product);
            card.querySelectorAll('script').forEach(script => script.remove());
            cards.set(product.variantId, card);
          }
          next = catalog.dataset.nextUrl;
        }
        if (!products.size || products.size !== Number(this.dataset.count)) throw Error('Collection count changed; existing browser retained');
        if (!this.isConnected) return;
        this.sources = document.createElement('div');
        this.sources.hidden = true;
        this.sources.style.setProperty('display', 'none', 'important');
        this.sources.dataset.recordBinSources = '';
        const scope = this.closest('[data-mix-scope]');
        for (const [id, card] of cards) {
          // Keep existing page cards and append only the missing pages.
          if (!scope.querySelector(`[data-original-mix-add][data-design-id="${id}"]`)) this.sources.append(card);
        }
        scope.append(this.sources);
        const shadow = this.attachShadow({mode:'open'});
        shadow.append(this.querySelector('template[data-record-bin-ui]').content.cloneNode(true));
        const style = document.createElement('link'); style.rel = 'stylesheet'; style.href = this.dataset.stylesheet;
        const loaded = new Promise((resolve, reject) => { style.onload = resolve; style.onerror = reject; });
        shadow.prepend(style);
        await loaded;
        if (!this.isConnected) return;
        this.cardGrid = document.createElement('div');
        this.cardGrid.className = 'bc-browse-grid';
        this.cardGrid.slot = 'original-product-cards';
        this.append(this.cardGrid);
        const displayCards = new Map();
        const renderOriginalCards = (filtered, container) => {
          if (!container.querySelector('slot[name="original-product-cards"]')) {
            const slot = document.createElement('slot');
            slot.name = 'original-product-cards';
            container.replaceChildren(slot);
            container.style.display = 'block';
          }
          const items = filtered.map(product => {
            let card = displayCards.get(product.variantId);
            if (!card) {
              card = cards.get(product.variantId).cloneNode(true);
              card.querySelectorAll('[data-product-preview]').forEach(button => button.hidden = false);
              displayCards.set(product.variantId, card);
            }
            return card;
          });
          this.cardGrid.replaceChildren(...items);
          const mix = document.querySelector('bc-originals-mix[data-global-mix]') || scope.querySelector('bc-originals-mix');
          mix?.refreshCards?.();
          scope.dispatchEvent(new CustomEvent('bc:designs-rendered', { bubbles: true }));
        };
        this.favoriteGrid = document.createElement('div');
        this.favoriteGrid.className = 'bc-browse-grid bc-support-carousel';
        this.favoriteGrid.slot = 'original-favorite-cards';
        this.append(this.favoriteGrid);
        const catalogProducts = [...products.values()];
        const recommendations = document.createElement('section');
        recommendations.className = 'recommendations-section'; recommendations.hidden = true;
        recommendations.setAttribute('aria-labelledby', 'recommendations-heading');
        recommendations.innerHTML = '<h2 id="recommendations-heading">More your kind of weird</h2><p>Inspired by the subjects and themes in your favorites.</p><slot name="original-recommendation-cards"></slot>';
        shadow.getElementById('favorites-section').after(recommendations);
        this.recommendationGrid = document.createElement('div');
        this.recommendationGrid.className = 'bc-browse-grid bc-support-carousel';
        this.recommendationGrid.slot = 'original-recommendation-cards';
        this.append(this.recommendationGrid);
        const carouselUpdates = [];
        const setupCarousel = (grid, section, label, before) => {
          grid.tabIndex = 0; grid.setAttribute('role', 'region');
          grid.setAttribute('aria-label', label + ' — scroll to browse');
          const controls = document.createElement('div'); controls.className = 'support-carousel-controls';
          const previous = document.createElement('button'), next = document.createElement('button');
          previous.type = next.type = 'button'; previous.textContent = '←'; next.textContent = '→';
          previous.setAttribute('aria-label', 'Previous ' + label); next.setAttribute('aria-label', 'Next ' + label);
          controls.append(previous, next); section.insertBefore(controls, before);
          const update = () => {
            const max = grid.scrollWidth - grid.clientWidth;
            controls.hidden = max <= 2; previous.disabled = grid.scrollLeft <= 2; next.disabled = grid.scrollLeft >= max - 2;
          };
          const move = direction => grid.scrollBy({left: direction * (grid.firstElementChild?.getBoundingClientRect().width + parseFloat(getComputedStyle(grid).columnGap) || grid.clientWidth * .8), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
          previous.onclick = () => move(-1); next.onclick = () => move(1);
          grid.addEventListener('scroll', update, {passive:true});
          grid.addEventListener('keydown', event => { if (event.target !== grid || !['ArrowLeft','ArrowRight'].includes(event.key)) return; event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1); });
          window.addEventListener('resize', update, {signal:this.abort.signal});
          carouselUpdates.push(update);
        };
        const favoritesSection = shadow.getElementById('favorites-section');
        setupCarousel(this.favoriteGrid, favoritesSection, 'favorites', shadow.getElementById('favorites-tray'));
        setupCarousel(this.recommendationGrid, recommendations, 'suggested designs', recommendations.querySelector('slot'));
        const recommend = saved => {
          const savedIds = new Set(saved.map(p => p.id));
          const interests = p => p.tags.filter(t => /^(subject-|theme-)/.test(t));
          const candidates = catalogProducts.filter(p => p.available && !savedIds.has(p.id)).map(product => {
            const tags = interests(product);
            const score = saved.reduce((sum, favorite) => {
              const shared = tags.filter(t => favorite.tags.includes(t));
              const subject = shared.some(t => t.startsWith('subject-'));
              const theme = shared.some(t => t.startsWith('theme-'));
              return sum + shared.length * 2 + (subject && theme ? 4 : 0);
            }, 0);
            return { product, tags, score };
          }).filter(p => p.score > 0);
          const selected = [], used = new Map();
          while (selected.length < 6 && candidates.length) {
            candidates.sort((a,b) => (b.score / (1 + b.tags.reduce((n,t) => n + (used.get(t) || 0),0) * .3)) - (a.score / (1 + a.tags.reduce((n,t) => n + (used.get(t) || 0),0) * .3)) || a.product.id.localeCompare(b.product.id));
            const next = candidates.shift(); selected.push(next.product);
            next.tags.forEach(t => used.set(t, (used.get(t) || 0) + 1));
          }
          return selected;
        };
        const renderFavoriteCards = (products, container, removeFavorite) => {
          if (!container.querySelector('slot[name="original-favorite-cards"]')) {
            const slot = document.createElement('slot'); slot.name = 'original-favorite-cards';
            container.replaceChildren(slot); container.style.display = 'block';
          }
          this.favoriteGrid.replaceChildren(...products.map(product => {
            const card = cards.get(product.variantId).cloneNode(true);
            const preview = card.querySelector('[data-product-preview]');
            if (preview) preview.hidden = false;
            card.querySelector('.bs-single-link')?.addEventListener('click', event => {
              event.preventDefault(); preview?.click();
            });
            const remove = document.createElement('button'); remove.type = 'button';
            remove.textContent = 'Remove favorite';
            remove.setAttribute('aria-label', `Remove ${product.title} from favorites`);
            remove.style.cssText = "background:transparent;border:0;color:#303730;font:12px 'Plus Jakarta Sans',Arial,sans-serif;min-height:36px;padding:8px;cursor:pointer";
            remove.onclick = () => { removeFavorite(product); shadow.getElementById('favorites-section').focus({preventScroll:true}); };
            card.querySelector('.set-info').append(remove);
            return card;
          }));
          const suggested = recommend(products);
          recommendations.hidden = !suggested.length;
          this.recommendationGrid.replaceChildren(...suggested.map(product => {
            const card = cards.get(product.variantId).cloneNode(true);
            const preview = card.querySelector('[data-product-preview]');
            if (preview) preview.hidden = false;
            card.querySelector('.bs-single-link')?.addEventListener('click', event => { event.preventDefault(); preview?.click(); });
            const save = document.createElement('button'); save.type = 'button';
            save.textContent = '♥ Save favorite';
            save.setAttribute('aria-label', `Save ${product.title} to favorites`);
            save.style.cssText = "background:transparent;border:0;color:#303730;font:12px 'Plus Jakarta Sans',Arial,sans-serif;min-height:40px;padding:8px;cursor:pointer";
            save.onclick = () => { removeFavorite(product); shadow.getElementById('favorites-section').focus({preventScroll:true}); };
            card.querySelector('.set-info').append(save);
            return card;
          }));
          const mix = document.querySelector('bc-originals-mix[data-global-mix]') || scope.querySelector('bc-originals-mix');
          mix?.refreshCards?.();
          scope.dispatchEvent(new CustomEvent('bc:designs-rendered', {bubbles:true}));
          requestAnimationFrame(() => carouselUpdates.forEach(update => update()));
        };
        this.browser = window.BullseyeRecordBin.mount({
          root: shadow, surface: this, catalog: { products: [...products.values()] },
          imageHosts: [location.hostname], storagePrefix: 'bullseye-originals-record-bin',
          renderGrid: renderOriginalCards,
          renderFavorites: renderFavoriteCards,
          onOpen: product => {
            const original = scope.querySelector(`[data-original-mix-add][data-design-id="${product.variantId}"]`);
            original?.closest('article')?.querySelector('[data-product-preview]')?.click();
          },
          onAdd: product => {
            const original = scope.querySelector(`[data-original-mix-add][data-design-id="${product.variantId}"]`);
            const mix = document.querySelector('bc-originals-mix[data-global-mix]') || scope.querySelector('bc-originals-mix');
            if (!product.available || !original || !mix?.add || mix.busy || mix.fillBusy) return false;
            // Reuse the existing component; its signed order and cart flow are unchanged.
            mix.rememberCard(original);
            return mix.add(product.variantId) === true;
          }
        });
        if (!this.browser || this.dataset.ready !== 'true') throw Error('Browser did not initialize');
        const panel = shadow.querySelector('.catalog-filter-panel');
        const controls = document.createElement('div'); controls.className = 'originals-filter-controls';
        const wrapField = (text, input) => {
          const label = document.createElement('label'); label.className = 'originals-filter-field';
          const caption = document.createElement('span'); caption.textContent = text;
          label.append(caption, input); return label;
        };
        const subjectSelect = document.createElement('select'); subjectSelect.id = 'subject-filter';
        shadow.querySelectorAll('[data-subject]').forEach(button => {
          const option = document.createElement('option'); option.value = button.dataset.subject;
          option.textContent = option.value ? button.textContent : 'All subjects';
          option.selected = button.getAttribute('aria-pressed') === 'true'; subjectSelect.append(option);
        });
        subjectSelect.onchange = () => [...shadow.querySelectorAll('[data-subject]')].find(button => button.dataset.subject === subjectSelect.value)?.click();
        controls.append(wrapField('Subject', subjectSelect), wrapField('Theme', shadow.getElementById('theme-filter')), wrapField('Sort by', shadow.getElementById('record-bin-sort')));
        shadow.querySelector('.category-chips').hidden = true;
        shadow.querySelector('.filter-row > label')?.remove();
        shadow.querySelector('.record-bin-options > label:not(.motion-option)')?.remove();
        panel.insertBefore(controls, shadow.querySelector('.filter-row'));
        const searchLabel = shadow.querySelector('.search-label');
        searchLabel.querySelector('input').placeholder = 'Search names or numbers';
        const searchCaption = searchLabel.querySelector('.sr-only'); searchCaption.className = 'search-caption'; searchCaption.textContent = 'Search designs';
        const options = shadow.querySelector('.record-bin-options'); panel.append(options);
        const disclosure = document.createElement('details');
        const summary = document.createElement('summary');
        summary.className = 'filter-disclosure-summary';
        summary.innerHTML = '<span>Search &amp; filters</span><span id="filter-summary-state"></span><svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>';
        const filterBody = document.createElement('div'); filterBody.className = 'filter-disclosure-body';
        filterBody.append(...panel.childNodes);
        disclosure.append(summary, filterBody); panel.append(disclosure);
        const syncSummary = () => {
          const count = shadow.getElementById('filtered-count').textContent;
          shadow.getElementById('filter-summary-state').textContent = count ? count.split(' · ')[0] : 'All designs';
        };
        this.filterSummaryObserver = new MutationObserver(syncSummary);
        this.filterSummaryObserver.observe(shadow.getElementById('filtered-count'), {childList:true,characterData:true,subtree:true});
        syncSummary();

        const oldHero = scope.querySelector('.bc-category-header');
        const title = document.createElement('h1'); title.className = 'sr-only'; title.textContent = 'Bullseye Originals';
        const heroHeading = document.createElement('header'); heroHeading.className = 'bin-hero-heading';
        const brand = document.createElement('img'); brand.className = 'bin-hero-logo';
        brand.src = new URL('bc-originals-watercolor-20261026.webp', new URL(this.dataset.stylesheet, location.href)).href;
        brand.width = 2172; brand.height = 724; brand.alt = 'Bullseye Originals';
        heroHeading.append(brand);
        shadow.getElementById('browser').prepend(title, heroHeading);
        shadow.getElementById('browser').insertBefore(panel, shadow.getElementById('grid-view'));
        shadow.querySelector('.stage-backdrop').hidden = true;
        const offer = document.createElement('aside'); offer.className = 'originals-offer';
        offer.innerHTML = '<div><strong>20 COASTERS. 20 BUCKS.</strong><span>Your picks. Your mix. Use code <b>20FOR20</b> at checkout.</span><small>Originals mixes · Cannot combine with other discounts.</small></div><button type="button" class="offer-copy" aria-label="Copy discount code 20FOR20">Copy 20FOR20</button><span class="sr-only" role="status"></span>';
        offer.querySelector('button').onclick = async () => {
          try { await navigator.clipboard.writeText('20FOR20'); offer.querySelector('button').textContent = 'Copied!'; offer.querySelector('[role=status]').textContent = 'Discount code 20FOR20 copied.'; }
          catch { offer.querySelector('[role=status]').textContent = 'Enter code 20FOR20 at checkout.'; }
        };
        shadow.getElementById('browser').insertBefore(offer, panel);


        const sortControl = shadow.getElementById('record-bin-sort');
        sortControl.value = sort || this.dataset.defaultSort || 'manual';
        sortControl.onchange = () => {
          const url = new URL(this.dataset.collectionUrl, location.origin);
          url.searchParams.set('sort_by', sortControl.value); location.assign(url.href);
        };
        const filters = scope.querySelector('bc-category-filters');
        if (filters?.id) { this.originalFilter = filters; this.id = filters.id; filters.removeAttribute('id'); }
        // Sorting remains available in the new browser. Existing tagged/search routes
        // retain their original controls until this enhancement is ready.
        const subject = this.dataset.initialSubject, mood = this.dataset.initialTheme;
        if (subject) shadow.querySelector(`[data-subject="${CSS.escape(subject)}"]`)?.click();
        if (mood) { shadow.getElementById('theme-filter').value = mood; shadow.getElementById('theme-filter').dispatchEvent(new Event('change')); }
        for (const el of [oldHero, filters, ...scope.querySelectorAll('[data-surprise-catalog], .bc-results-count, .bc-pagination')].filter(Boolean)) {
          this.hiddenElements.push([el, el.hidden, el.style.getPropertyValue('display'), el.style.getPropertyPriority('display')]);
          el.hidden = true;
          el.style.setProperty('display', 'none', 'important');
        }
        this.hidden = false;
        document.documentElement.classList.remove('bc-originals-loading');
        clearTimeout(window.bcOriginalsLoadingTimeout);
        const mix = document.querySelector('bc-originals-mix[data-global-mix]') || scope.querySelector('bc-originals-mix');
        mix?.refreshCards?.();
        document.dispatchEvent(new CustomEvent('bc:designs-rendered'));
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('Record-bin preview kept the existing collection browser:', error.message);
        this.restore();
      } finally { clearTimeout(timeout); }
    }
    restore() {
      this.filterSummaryObserver?.disconnect();
      document.documentElement.classList.remove('bc-originals-loading');
      clearTimeout(window.bcOriginalsLoadingTimeout);
      this.browser?.destroy(); this.browser = null;
      for (const [el, hidden, display, priority] of this.hiddenElements || []) {
        el.hidden = hidden;
        if (display) el.style.setProperty('display', display, priority);
        else el.style.removeProperty('display');
      }
      if (this.originalFilter && this.id) { this.originalFilter.id = this.id; this.removeAttribute('id'); }
      this.cardGrid?.remove();
      this.favoriteGrid?.remove();
      this.recommendationGrid?.remove();
      this.sources?.remove(); this.hidden = true;
    }
    disconnectedCallback() { this.abort?.abort(); this.restore(); }
  }
  if (!customElements.get('bc-originals-record-bin')) customElements.define('bc-originals-record-bin', OriginalsRecordBin);
})();
